import React from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, CalendarOff, Loader2, Settings } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import type { ChipState, ScheduleStrategySummary } from '../../types/schedule';
import {
  computeStrategyRunState,
  computeStrategySlChip,
  computeStrategyOverallStatus,
  type StrategyRunStateChip,
} from '../../utils/scheduleChip';
import { StatusBoxRow } from '../common/StatusBoxRow';
import { formatTimeRange, formatTradingType } from '../../utils/scheduleSummary';
import { useColumnOrder } from '../../hooks/useColumnOrder';
import { useAuth } from '../../context/AuthContext';
import DraggableHeader from './DraggableHeader';
import { STATUS_VARIANT_CLASSES } from '../../utils/statusColors';
import PnlFlash from '../common/PnlFlash';
import EmptyStateWithRedirect from '../common/EmptyStateWithRedirect';
import { pickScheduleEmpty } from '../../utils/emptyStates';
import SLPendingPill from './SLPendingPill';
import {
  STATUS_OPTIONS,
  STATE_OPTIONS,
  hasActiveScheduleFilters,
  type ScheduleColumnFilters,
  type ScheduleStatusFilter,
  type ScheduleStateFilter,
  type LabeledOption,
} from '../../utils/scheduleFilters';
import ColumnFilterButton from './ColumnFilterButton';
import BrokerBreakdownLine from './shared/BrokerBreakdownLine';
import { expiryTypeLabel } from '../../utils/strategy/expiryDisplay';
import BasketNameLink from '../basket/BasketNameLink';

type SortColumn =
  | 'time' | 'basketStrategy' | 'state' | 'status' | 'underlying' | 'expiry' | 'product' | 'trading'
  | 'legs' | 'sl' | 'pnl';
type SortDir = 'asc' | 'desc';

const DEFAULT_COLUMN_ORDER: readonly SortColumn[] = [
  'time', 'basketStrategy', 'state', 'status', 'underlying', 'expiry', 'product', 'trading',
  'legs', 'sl', 'pnl',
] as const;
const COLUMN_LABELS: Record<SortColumn, string> = {
  time: 'Time',
  basketStrategy: 'Basket / Strategy',
  state: 'State',
  status: 'Health',
  underlying: 'Underlying',
  expiry: 'Expiry',
  product: 'Product',
  trading: 'Trading',
  legs: 'Legs',
  sl: 'SL',
  pnl: 'P&L',
};

/** Sort order for the Health column — errors first, closed last. */
function overallStatusRank(status: string): number {
  switch (status) {
    case 'ERROR':     return 0;
    case 'PENDING':   return 1;
    case 'ACTIVE':    return 2;
    case 'SCHEDULED': return 3;
    case 'CLOSED':    return 4;
    default:          return 5;
  }
}

/** Sort order for the State column — ERROR first, CLOSED last (matches the
 *  user's mental model of "stuff to attend to" sitting at the top). */
function stateRank(state: string): number {
  switch (state) {
    case 'ERROR':     return 0;
    case 'RUNNING':   return 1;
    case 'SCHEDULED': return 2;
    case 'CLOSED':    return 3;
    default:          return 4;
  }
}

function chipVariantRank(chip: ChipState | null): number {
  if (!chip) return 5;
  switch (chip.variant) {
    case 'red': return 0;
    case 'yellow': return 1;
    case 'green': return 2;
    case 'gray': return 3;
    default: return 4;
  }
}

interface ScheduleTableProps {
  /** Strategies AFTER the view-level search + status filters. */
  strategies: ScheduleStrategySummary[];
  /** Unfiltered day total — used to distinguish "nothing scheduled" from
   *  "nothing matches filters" in the empty state. */
  totalStrategyCount: number;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  /** Per-column multi-select filter state owned by the parent view. */
  columnFilters: ScheduleColumnFilters;
  /** Replace the column filter state — caller merges / clears as needed. */
  onColumnFiltersChange: (next: ScheduleColumnFilters) => void;
  /** Dropdown options per filterable column, computed from the unfiltered list. */
  filterOptions: {
    baskets:       LabeledOption<string>[];
    underlyings:   LabeledOption<string>[];
    expiries:      LabeledOption<string>[];
    products:      LabeledOption<string>[];
    tradingTypes:  LabeledOption<string>[];
  };
  /** Status-column counts, pre-computed against rows that already pass the
   *  other column filters (so the badges reflect what the user would see). */
  statusCounts: Record<ScheduleStatusFilter, number>;
  /** State-column counts, same precomputation pattern as statusCounts. */
  stateCounts: Record<ScheduleStateFilter, number>;
  selectedDate: string;
  isToday: boolean;
  isPast: boolean;
  isTradingDay: boolean;
  currentIstHms: string | null;
  loading?: boolean;
  refreshing?: boolean;
  error?: string | null;
  onOpenStrategy: (groupKey: string) => void;
  onOpenBasket: (basketId: string) => void;
  onEditStrategy: (strategyId: string, basketId: string) => void;
  /** SL placements still awaiting morning re-placement; renders a small pill in the filter bar. */
  slPendingCount?: number;
}

// Canonical palette shared with positions / orders / modals.
const CHIP_CLASSES = STATUS_VARIANT_CLASSES;

function Chip({ state }: { state: ChipState | null }) {
  if (!state) return <span className="text-gray-300 dark:text-gray-600 tabular-nums">—</span>;
  return (
    <span
      title={state.subtext || undefined}
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap',
        CHIP_CLASSES[state.variant],
      )}
    >
      {state.label}
    </span>
  );
}

function keyOf(s: ScheduleStrategySummary): string {
  return `${s.basket_id}#${s.strategy_id}`;
}

function formatBrokerLine(brokers: string[]): string {
  if (brokers.length === 0) return '';
  if (brokers.length === 1) return brokers[0];
  return `${brokers.length} brokers`;
}

function compareStrategies(
  a: ScheduleStrategySummary, b: ScheduleStrategySummary, col: SortColumn,
  aState: StrategyRunStateChip, bState: StrategyRunStateChip,
  aSl: ChipState | null, bSl: ChipState | null,
): number {
  switch (col) {
    case 'time': {
      const at = a.scheduled_entry_time || a.scheduled_exit_time || '99:99';
      const bt = b.scheduled_entry_time || b.scheduled_exit_time || '99:99';
      return at.localeCompare(bt);
    }
    case 'basketStrategy': {
      // Primary by strategy name (the highlighted one), tiebreak by basket.
      const c = a.strategy_name.localeCompare(b.strategy_name);
      return c !== 0 ? c : a.basket_name.localeCompare(b.basket_name);
    }
    case 'state':      return stateRank(aState.state) - stateRank(bState.state);
    case 'status':     return overallStatusRank(computeStrategyOverallStatus(a))
      - overallStatusRank(computeStrategyOverallStatus(b));
    case 'underlying': return a.underlying.localeCompare(b.underlying);
    case 'expiry':     return (a.expiry_type || '').localeCompare(b.expiry_type || '');
    case 'product':    return (a.product_type || '').localeCompare(b.product_type || '');
    case 'trading':    return formatTradingType(a.trading_type, a.intraday_exit_mode)
      .localeCompare(formatTradingType(b.trading_type, b.intraday_exit_mode));
    case 'legs':       return (a.total_effective_lots ?? a.total_lots) - (b.total_effective_lots ?? b.total_lots);
    case 'sl':         return chipVariantRank(aSl) - chipVariantRank(bSl);
    case 'pnl':        return (a.total_pnl ?? 0) - (b.total_pnl ?? 0);
    default:           return 0;
  }
}

const ScheduleTable: React.FC<ScheduleTableProps> = ({
  strategies,
  totalStrategyCount,
  searchQuery,
  onSearchChange,
  columnFilters,
  onColumnFiltersChange,
  filterOptions,
  statusCounts,
  stateCounts,
  selectedDate,
  isToday,
  isPast,
  isTradingDay,
  currentIstHms,
  loading = false,
  refreshing = false,
  error,
  onOpenStrategy,
  onOpenBasket,
  onEditStrategy,
  slPendingCount = 0,
}) => {
  const [sortColumn, setSortColumn] = React.useState<SortColumn | null>(null);
  const [sortDir, setSortDir] = React.useState<SortDir>('asc');

  const { user } = useAuth();
  const storageKey = user?.id ? `schedule:classic:column-order:${user.id}` : null;
  const { order: columnOrder, moveColumn, reset: resetColumns, isDefault: columnsAreDefault } =
    useColumnOrder<SortColumn>(storageKey, DEFAULT_COLUMN_ORDER);

  const handleSort = React.useCallback((col: SortColumn) => {
    setSortColumn((prev) => {
      if (prev === col) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir('asc');
      return col;
    });
  }, []);

  // Wrap in useMemo so downstream useMemos (chipIndex below) don't re-run
  // every render — the object literal otherwise produces a new reference on
  // each render and invalidates the dep array (react-hooks/exhaustive-deps).
  const chipCtx = React.useMemo(
    () => ({ isToday, isPast, nowIstHms: currentIstHms }),
    [isToday, isPast, currentIstHms],
  );

  // `strategies` is already filtered by the view (search + status). Chip
  // computation and sort run directly on the incoming list.
  const chipIndex = React.useMemo(() => {
    const m = new Map<string, { state: StrategyRunStateChip; sl: ChipState | null }>();
    for (const s of strategies) {
      m.set(keyOf(s), {
        state: computeStrategyRunState(s, chipCtx),
        sl: computeStrategySlChip(s),
      });
    }
    return m;
  }, [strategies, chipCtx]);

  const sorted = React.useMemo(() => {
    if (!sortColumn) return strategies;
    const mul = sortDir === 'asc' ? 1 : -1;
    // Fallback state for rows that somehow miss the chipIndex map — defensive
    // gray SCHEDULED keeps sort stable rather than throwing.
    const FALLBACK_STATE: StrategyRunStateChip = {
      state: 'SCHEDULED', variant: 'gray', label: 'Scheduled',
    };
    return [...strategies].sort((a, b) => {
      const ac = chipIndex.get(keyOf(a)) ?? { state: FALLBACK_STATE, sl: null };
      const bc = chipIndex.get(keyOf(b)) ?? { state: FALLBACK_STATE, sl: null };
      return compareStrategies(
        a, b, sortColumn,
        ac.state, bc.state, ac.sl, bc.sl,
      ) * mul;
    });
  }, [strategies, sortColumn, sortDir, chipIndex]);

  const filtersActive = hasActiveScheduleFilters(searchQuery, columnFilters);

  const resetColumnFilter = <K extends keyof ScheduleColumnFilters>(key: K, next: ScheduleColumnFilters[K]) => {
    onColumnFiltersChange({ ...columnFilters, [key]: next });
  };
  /** Wipe search + every column filter in one shot. Shared between the
   *  header "Clear all" button and the in-table / in-card empty-state CTA. */
  const clearAllFilters = React.useCallback(() => {
    onSearchChange('');
    onColumnFiltersChange({
      baskets: new Set(),
      state: new Set(),
      status: new Set(),
      underlyings: new Set(),
      expiries: new Set(),
      products: new Set(),
      tradingTypes: new Set(),
    });
  }, [onSearchChange, onColumnFiltersChange]);

  if (!isTradingDay) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Execution Schedule ({selectedDate})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="py-16 text-center text-sm text-gray-500 dark:text-gray-400">
            Market closed — not a trading day.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3 flex-wrap">
          <CardTitle className="shrink-0 inline-flex items-center gap-2">
            <span>[{sorted.length}] Strategies</span>
            {/* Fixed-width spinner keeps header width stable so the search /
             *  filter row never reflows when refreshing toggles on or off. */}
            <Loader2
              aria-label={refreshing ? 'Refreshing' : undefined}
              className={cn(
                'w-3.5 h-3.5 text-gray-400 shrink-0 transition-opacity',
                refreshing ? 'animate-spin opacity-100' : 'opacity-0',
              )}
            />
          </CardTitle>
          {/* Sort-state indicator — always visible so row ordering is never
              a mystery. Classic has no attention-first default, so unsorted
              rows read "Default order"; column-driven sorts read e.g.
              "P&L ↓". Mirrors the Pro view's identical pill. */}
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded shrink-0"
            title="Click any column header to sort by that column"
          >
            Sorted by: {sortColumn === null
              ? 'Default order'
              : `${COLUMN_LABELS[sortColumn]} ${sortDir === 'asc' ? '↑' : '↓'}`}
          </span>
          {filtersActive && (
            <button
              type="button"
              onClick={clearAllFilters}
              className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline shrink-0"
              title="Clear search and column filters"
            >
              Clear all
            </button>
          )}
          {sortColumn !== null && (
            <button
              type="button"
              onClick={() => setSortColumn(null)}
              className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline shrink-0"
              title="Reset to default order"
            >
              Reset sort
            </button>
          )}
          {!columnsAreDefault && (
            <button
              type="button"
              onClick={resetColumns}
              className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline shrink-0"
              title="Restore the default column order"
            >
              Reset columns
            </button>
          )}
          <div className="flex items-center gap-3 ml-auto shrink-0">
            <SLPendingPill count={slPendingCount} />
          </div>
          {/* Phase B3: page-level toolbar above the view owns Search, IST
              clock, Refresh, and Exit All. `searchQuery` / `onSearchChange`
              still arrive as props so `filtersActive` / `clearAllFilters`
              can integrate the search into "Clear all". */}
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {error && (
          <div className="px-6 py-3 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 text-sm text-red-800 dark:text-red-200">
            {error}
          </div>
        )}

        {loading ? (
          <div className="py-16 text-center text-sm text-gray-500 dark:text-gray-400">Loading…</div>
        ) : totalStrategyCount === 0 ? (
          /* Day has no strategies at all — headers are moot; show a
             context-aware CTA pointing the user to baskets (today) or
             a "you weren't trading" hint (past). */
          <EmptyStateWithRedirect
            icon={<CalendarOff className="w-8 h-8" />}
            {...pickScheduleEmpty({ isToday, isPast: !isToday })}
          />
        ) : (
          /* Filters may be hiding everything; always render the table so the
           * header (and its filter icons) stays reachable. When the filtered
           * list is empty, the table body + mobile cards show an inline
           * empty-state row with a one-click "Clear all filters" affordance. */
          <>
            <DesktopTable
              strategies={sorted}
              chipIndex={chipIndex}
              sortColumn={sortColumn}
              sortDir={sortDir}
              onSort={handleSort}
              onOpenStrategy={onOpenStrategy}
              onOpenBasket={onOpenBasket}
              onEditStrategy={onEditStrategy}
              columnOrder={columnOrder}
              onMoveColumn={moveColumn}
              columnFilters={columnFilters}
              onSetColumnFilter={resetColumnFilter}
              filterOptions={filterOptions}
              statusCounts={statusCounts}
              stateCounts={stateCounts}
              onClearAllFilters={clearAllFilters}
            />
            <MobileCards
              strategies={sorted}
              chipIndex={chipIndex}
              onOpenStrategy={onOpenStrategy}
              onOpenBasket={onOpenBasket}
              onEditStrategy={onEditStrategy}
              onClearAllFilters={clearAllFilters}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ── Inner desktop / mobile ─────────────────────────────────────────────────

interface InnerProps {
  strategies: ScheduleStrategySummary[];
  chipIndex: Map<string, { state: StrategyRunStateChip; sl: ChipState | null }>;
  onOpenStrategy: (groupKey: string) => void;
  onOpenBasket: (basketId: string) => void;
  onEditStrategy: (strategyId: string, basketId: string) => void;
  /** Wipes search + every column filter. Shown as the CTA in the inline
   *  empty-state row when filters have hidden every strategy. */
  onClearAllFilters: () => void;
}

interface DesktopProps extends InnerProps {
  sortColumn: SortColumn | null;
  sortDir: SortDir;
  onSort: (col: SortColumn) => void;
  columnOrder: SortColumn[];
  onMoveColumn: (from: SortColumn, to: SortColumn) => void;
  columnFilters: ScheduleColumnFilters;
  onSetColumnFilter: <K extends keyof ScheduleColumnFilters>(key: K, next: ScheduleColumnFilters[K]) => void;
  filterOptions: {
    baskets:       LabeledOption<string>[];
    underlyings:   LabeledOption<string>[];
    expiries:      LabeledOption<string>[];
    products:      LabeledOption<string>[];
    tradingTypes:  LabeledOption<string>[];
  };
  statusCounts: Record<ScheduleStatusFilter, number>;
  stateCounts: Record<ScheduleStateFilter, number>;
}

/**
 * Renders the correct column filter for a given SortColumn, or nothing for
 * columns we don't want filterable (time, sl chip, legs, pnl).
 * Centralised so the header map stays tidy.
 */
const HeaderFilterSlot: React.FC<{
  col: SortColumn;
  columnFilters: ScheduleColumnFilters;
  onSetColumnFilter: <K extends keyof ScheduleColumnFilters>(key: K, next: ScheduleColumnFilters[K]) => void;
  filterOptions: DesktopProps['filterOptions'];
  statusCounts: Record<ScheduleStatusFilter, number>;
  stateCounts: Record<ScheduleStateFilter, number>;
}> = ({ col, columnFilters, onSetColumnFilter, filterOptions, statusCounts, stateCounts }) => {
  switch (col) {
    case 'basketStrategy':
      return (
        <ColumnFilterButton<string>
          items={filterOptions.baskets}
          selected={columnFilters.baskets}
          onChange={(next) => onSetColumnFilter('baskets', next)}
          title="Filter by basket"
          ariaLabel="Filter basket"
        />
      );
    case 'state':
      return (
        <ColumnFilterButton<ScheduleStateFilter>
          items={STATE_OPTIONS.map((opt) => ({
            value: opt.value,
            label: opt.label,
            count: stateCounts[opt.value],
            leftContent: <span className={cn('w-2 h-2 rounded-full shrink-0', opt.dotClass)} />,
            labelClass: opt.labelClass,
          }))}
          selected={columnFilters.state}
          onChange={(next) => onSetColumnFilter('state', next)}
          title="Filter by state"
          ariaLabel="Filter state"
          searchable={false}
        />
      );
    case 'status':
      return (
        <ColumnFilterButton<ScheduleStatusFilter>
          items={STATUS_OPTIONS.map((opt) => ({
            value: opt.value,
            label: opt.label,
            count: statusCounts[opt.value],
            leftContent: <span className={cn('w-2 h-2 rounded-full shrink-0', opt.dotClass)} />,
            labelClass: opt.labelClass,
          }))}
          selected={columnFilters.status}
          onChange={(next) => onSetColumnFilter('status', next)}
          title="Filter by health"
          ariaLabel="Filter health"
          searchable={false}
        />
      );
    case 'underlying':
      return (
        <ColumnFilterButton<string>
          items={filterOptions.underlyings}
          selected={columnFilters.underlyings}
          onChange={(next) => onSetColumnFilter('underlyings', next)}
          title="Filter by underlying"
          ariaLabel="Filter underlying"
        />
      );
    case 'expiry':
      return (
        <ColumnFilterButton<string>
          items={filterOptions.expiries}
          selected={columnFilters.expiries}
          onChange={(next) => onSetColumnFilter('expiries', next)}
          title="Filter by expiry"
          ariaLabel="Filter expiry"
        />
      );
    case 'product':
      return (
        <ColumnFilterButton<string>
          items={filterOptions.products}
          selected={columnFilters.products}
          onChange={(next) => onSetColumnFilter('products', next)}
          title="Filter by product"
          ariaLabel="Filter product"
        />
      );
    case 'trading':
      return (
        <ColumnFilterButton<string>
          items={filterOptions.tradingTypes}
          selected={columnFilters.tradingTypes}
          onChange={(next) => onSetColumnFilter('tradingTypes', next)}
          title="Filter by trading type"
          ariaLabel="Filter trading type"
        />
      );
    default:
      return null;
  }
};

const SortLabel: React.FC<{
  col: SortColumn;
  label: string;
  active: boolean;
  dir: SortDir;
  onSort: (col: SortColumn) => void;
}> = ({ col, label, active, dir, onSort }) => (
  <button
    type="button"
    onClick={() => onSort(col)}
    onMouseDown={(e) => e.stopPropagation()}
    onDragStart={(e) => e.preventDefault()}
    draggable={false}
    className={cn(
      'inline-flex items-center gap-1 hover:text-gray-700 dark:hover:text-white transition-colors',
      active && 'text-gray-700 dark:text-white',
    )}
  >
    {label}
    {active ? (
      dir === 'asc'
        ? <ArrowUp className="w-3 h-3" />
        : <ArrowDown className="w-3 h-3" />
    ) : (
      <ArrowUpDown className="w-3 h-3 opacity-40" />
    )}
  </button>
);

type CellRenderCtx = {
  chips: { state: StrategyRunStateChip; sl: ChipState | null } | undefined;
  onOpenStrategy: (groupKey: string) => void;
  onOpenBasket: (basketId: string) => void;
  /** Strategy-config editor launcher — wired through so the Settings icon
   *  can live inline in the basket/strategy cell instead of an extra column. */
  onEditStrategy: (strategyId: string, basketId: string) => void;
  groupKey: string;
};

/** Render a StrategyRunStateChip as a coloured pill. Mirrors `Chip` above but
 *  takes the state-specific shape so the tooltip and label flow naturally. */
function StateChip({ chip }: { chip: StrategyRunStateChip }) {
  return (
    <span
      title={chip.tooltip || undefined}
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap tabular-nums',
        CHIP_CLASSES[chip.variant],
      )}
    >
      {chip.label}
    </span>
  );
}

const CELL_RENDERERS: Record<SortColumn, (s: ScheduleStrategySummary, ctx: CellRenderCtx) => React.ReactNode> = {
  time: (s) => {
    // ORB strategies: `scheduled_entry_time` IS the ORB range-end time —
    // when the monitor evaluates and the order can be placed. Color only the
    // entry time in amber so it reads as the trigger moment, while keeping
    // the → exit time in the standard gray so the strategy's full window is
    // still visible. Amber matches platform-wide ORB colour (entrySource.ts).
    const isOrb = s.entry_trigger === 'ORB';
    if (isOrb && s.scheduled_entry_time) {
      const btstSuffix = s.intraday_exit_mode === 'NEXT_DAY_BTST' ? ' (next day)' : '';
      return (
        <div className="flex flex-col gap-0.5">
          <span className="text-xs tabular-nums">
            <span
              className="text-amber-700 dark:text-amber-300 font-semibold"
              title="ORB end time — order placed at this time"
            >
              {s.scheduled_entry_time}
            </span>
            {s.scheduled_exit_time && (
              <span className="text-gray-600 dark:text-gray-300">
                {' → '}{s.scheduled_exit_time}{btstSuffix}
              </span>
            )}
          </span>
          <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-600 dark:text-amber-400">
            ORB
          </span>
        </div>
      );
    }
    return (
      <span className="text-xs text-gray-600 dark:text-gray-300 tabular-nums">
        {formatTimeRange(s.scheduled_entry_time, s.scheduled_exit_time, s.intraday_exit_mode)}
      </span>
    );
  },
  basketStrategy: (s, { onOpenBasket, onOpenStrategy, onEditStrategy, groupKey }) => {
    // Marketplace subscriptions render in violet (via BasketNameLink) — visually
    // distinct from the standard blue link colour used for user-owned baskets,
    // so the subscriber can tell at a glance which rows are bound to a publisher's
    // basket. Click behaviour is unchanged: the basket name still opens the
    // basket overlay via onOpenBasket(s.basket_id) regardless of source.
    const isMkt = s.is_marketplace === true;
    const strategyClass = isMkt
      ? 'text-violet-700 dark:text-violet-300 hover:text-violet-800 dark:hover:text-violet-200'
      : 'text-blue-700 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200';
    return (
      <div className="flex flex-col gap-0.5 min-w-0">
        <BasketNameLink
          basketId={s.basket_id}
          basketName={s.basket_name || ''}
          isMarketplace={isMkt}
          onClick={() => onOpenBasket(s.basket_id)}
          size="sm"
          title={isMkt ? 'Marketplace subscription' : 'View all strategies in this basket'}
        />
        <div className="flex items-center gap-1.5 min-w-0">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEditStrategy(s.strategy_id, s.basket_id); }}
            className="p-1 -ml-1 rounded-md text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-200 transition-colors shrink-0"
            title="Edit strategy configuration"
            aria-label="Edit strategy configuration"
          >
            <Settings className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpenStrategy(groupKey); }}
            className={cn(
              'text-sm font-semibold hover:underline transition-colors text-left truncate min-w-0',
              strategyClass,
            )}
            title={isMkt ? 'Marketplace strategy — view all legs' : 'View all legs for this strategy'}
          >
            {s.strategy_name || '—'}
          </button>
        </div>
      </div>
    );
  },
  state: (_s, { chips }) => chips?.state ? <StateChip chip={chips.state} /> : <span className="text-gray-300 dark:text-gray-600">—</span>,
  status: (s) => <StatusBoxRow summary={s} />,
  underlying: (s) => {
    const brokerLine = formatBrokerLine(s.brokers);
    return (
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-xs text-gray-700 dark:text-gray-200">{s.underlying}</span>
        {brokerLine && (
          <span
            className="text-[10px] text-gray-400 dark:text-gray-500 truncate"
            title={s.brokers.join(' · ')}
          >
            {brokerLine}
          </span>
        )}
      </div>
    );
  },
  expiry: (s) => (
    <span className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap">
      {s.expiry_type ? expiryTypeLabel(s.expiry_type) : '—'}
    </span>
  ),
  product: (s) => (
    <span className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
      {s.product_type || '—'}
    </span>
  ),
  trading: (s) => (
    <span className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 inline-flex items-center gap-1">
      {formatTradingType(s.trading_type, s.intraday_exit_mode) || '—'}
      {s.expiry_day_rollover && (
        <span
          className="px-1 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-[9px] font-semibold"
          title="Rolls to next contract on the underlying's expiry day"
        >
          ROLLOVER
        </span>
      )}
    </span>
  ),
  legs: (s) => (
    <div className="flex flex-col gap-0.5 min-w-0">
      <span className="text-xs text-gray-700 dark:text-gray-200 tabular-nums">
        {s.leg_count} {s.leg_count === 1 ? 'leg' : 'legs'}
        <span className="ml-1 text-gray-500 dark:text-gray-400">· {s.total_effective_lots ?? s.total_lots}L</span>
      </span>
      <BrokerBreakdownLine items={s.broker_breakdown ?? []} />
    </div>
  ),
  sl:    (_s, { chips }) => <Chip state={chips?.sl ?? null} />,
  pnl: (s) => <PnlFlash value={s.total_pnl} className="text-xs font-medium" />,
};

const DATA_CELL_CLASS = 'px-3 py-3 whitespace-nowrap align-top';

const DesktopTable: React.FC<DesktopProps> = ({
  strategies, chipIndex, sortColumn, sortDir, onSort,
  onOpenStrategy, onOpenBasket, onEditStrategy,
  columnOrder, onMoveColumn,
  columnFilters, onSetColumnFilter, filterOptions, statusCounts, stateCounts,
  onClearAllFilters,
}) => (
  <div className="hidden md:block overflow-x-auto">
    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
      <thead className="bg-gray-50 dark:bg-gray-700">
        <tr>
          {columnOrder.map((col) => (
            <DraggableHeader key={col} colKey={col} onMove={onMoveColumn}>
              <span className="inline-flex items-center gap-1">
                <SortLabel
                  col={col}
                  label={COLUMN_LABELS[col]}
                  active={sortColumn === col}
                  dir={sortDir}
                  onSort={onSort}
                />
                <HeaderFilterSlot
                  col={col}
                  columnFilters={columnFilters}
                  onSetColumnFilter={onSetColumnFilter}
                  filterOptions={filterOptions}
                  statusCounts={statusCounts}
                  stateCounts={stateCounts}
                />
              </span>
            </DraggableHeader>
          ))}
        </tr>
      </thead>
      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
        {strategies.length === 0 && (
          <tr>
            <td colSpan={columnOrder.length} className="py-16 text-center text-sm text-gray-500 dark:text-gray-400">
              No strategies match the current filters.
              <button
                type="button"
                onClick={onClearAllFilters}
                className="ml-2 font-medium text-blue-600 dark:text-blue-400 hover:underline"
              >
                Clear all filters
              </button>
            </td>
          </tr>
        )}
        {strategies.map((s) => {
          const gKey = keyOf(s);
          const chips = chipIndex.get(gKey);
          const ctx: CellRenderCtx = { chips, onOpenStrategy, onOpenBasket, onEditStrategy, groupKey: gKey };
          return (
            <tr
              key={gKey}
              className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
            >
              {columnOrder.map((col) => (
                <td key={col} className={DATA_CELL_CLASS}>
                  {CELL_RENDERERS[col](s, ctx)}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const MobileCards: React.FC<InnerProps> = ({
  strategies,
  chipIndex,
  onOpenStrategy, onOpenBasket, onEditStrategy,
  onClearAllFilters,
}) => (
  <div className="md:hidden space-y-2 p-3">
    {strategies.length === 0 && (
      <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
        No strategies match the current filters.
        <button
          type="button"
          onClick={onClearAllFilters}
          className="block mx-auto mt-2 font-medium text-blue-600 dark:text-blue-400 hover:underline"
        >
          Clear all filters
        </button>
      </div>
    )}
    {strategies.map((s) => {
      const gKey = keyOf(s);
      const mobileChips = chipIndex.get(gKey);
      return (
        <div
          key={gKey}
          className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <BasketNameLink
                basketId={s.basket_id}
                basketName={s.basket_name || ''}
                isMarketplace={s.is_marketplace === true}
                onClick={() => onOpenBasket(s.basket_id)}
                size="sm"
                className="block"
              />
              <button
                type="button"
                onClick={() => onOpenStrategy(gKey)}
                className={cn(
                  'block text-left text-sm font-semibold hover:underline truncate w-full',
                  s.is_marketplace
                    ? 'text-violet-700 dark:text-violet-300'
                    : 'text-blue-700 dark:text-blue-300',
                )}
              >
                {s.strategy_name || '—'}
              </button>
            </div>
            <div className="flex items-start gap-2 shrink-0">
              <PnlFlash value={s.total_pnl} className="text-sm font-bold" />
              <button
                type="button"
                onClick={() => onEditStrategy(s.strategy_id, s.basket_id)}
                className="p-1 rounded-md text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                title="Edit strategy configuration"
                aria-label="Edit strategy configuration"
              >
                <Settings className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 flex-wrap">
            <span>
              {s.underlying}
              <span className="ml-1 text-[10px] uppercase text-gray-400 dark:text-gray-500 whitespace-nowrap">
                {expiryTypeLabel(s.expiry_type)}
                {s.product_type && ` · ${s.product_type}`}
              </span>
            </span>
            {s.trading_type && (
              <span className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500">
                · {formatTradingType(s.trading_type, s.intraday_exit_mode)}
              </span>
            )}
            <span>· {s.leg_count} {s.leg_count === 1 ? 'leg' : 'legs'} · {s.total_effective_lots ?? s.total_lots}L</span>
            <span className="ml-auto tabular-nums">
              {formatTimeRange(s.scheduled_entry_time, s.scheduled_exit_time, s.intraday_exit_mode)}
            </span>
          </div>
          <BrokerBreakdownLine items={s.broker_breakdown ?? []} />
          {/* State chip + health boxes — mobile shows what the desktop State
           *  and Health columns show, keeping both views mentally aligned. */}
          <div className="flex items-center gap-2 flex-wrap">
            {mobileChips?.state && <StateChip chip={mobileChips.state} />}
          </div>
          <StatusBoxRow summary={s} />
        </div>
      );
    })}
  </div>
);

export default ScheduleTable;